import { get, memoize } from 'lodash';
import config from 'Config';
import { isSeller } from 'Utils/seller/sellerUtils';
import {
  composeFiltersAsPayload,
  getDepartureCityFilters,
  getDepartureDateFilters,
  getDurationFilters,
  getFacetsFilters,
  getLocaleFilters,
  getOfferFilters,
  getProductIdsFilters,
} from './filters';
import { postSearchRequest } from './request';
const ACTIONS = {
  WORD_WHEEL: 'wordWheel',
  PERTIMM_SEARCH: 'pertimmSearch',
};
const getApiKey = memoize(function getApiKey(appContext: AppContext): string {
  return config.get(
    // eslint-disable-next-line no-nested-ternary
    appContext.isCmta
      ? 'api.cm_ta_api_key'
      : isSeller(appContext)
        ? 'api.seller.api_key'
        : 'api.api_key',
  );
});
type Params<Criteria> = {
  criteria?: Partial<Criteria>;
  appContext: AppContext;
};

function suggest({ appContext, criteria = {} }: Params<PertimmSearchCriteria>): Promise<any> {
  const { departureDate, keywords = '', flexibility } = criteria;
  return postSearchRequest({
    locale: appContext.locale,
    queryparams: {
      api_key: getApiKey(appContext),
      action: ACTIONS.WORD_WHEEL,
      max_suggests: config.get('pertimm.max_suggests'),
      enable_interpretation_explanation: 'true',
      query: keywords,
    },
    payload: composeFiltersAsPayload(
      getDepartureDateFilters({
        departureDate,
        flexibility,
        locale: appContext.locale,
      }),
    ),
  });
}

function search({ appContext, criteria = {} }: Params<PertimmSearchCriteria>): Promise<any> {
  const { departureDate, keywords = '', flexibility } = criteria;
  return postSearchRequest({
    locale: appContext.locale,
    queryparams: {
      api_key: getApiKey(appContext),
      action: ACTIONS.PERTIMM_SEARCH,
      max_files: config.get('pertimm.max_files'),
      format: 'json',
      query: keywords,
    },
    payload: composeFiltersAsPayload(
      getDepartureDateFilters({
        departureDate,
        flexibility,
        locale: appContext.locale,
      }),
      getLocaleFilters(appContext.locale),
    ),
  });
}

type ParamsSearchWithFacets = Params<PertimmSearchWithFacetCriteria>;

function searchWithFacets({ criteria = {}, appContext }: ParamsSearchWithFacets): Promise<any> {
  const { departureDate, flexibility } = criteria;
  return postSearchRequest({
    locale: appContext.locale,
    queryparams: {
      api_key: getApiKey(appContext),
      action: ACTIONS.PERTIMM_SEARCH,
      max_files: config.get('pertimm.max_files'),
      format: 'json',
      query: criteria.query || '',
      'contexts[boutique]': criteria.boutique,
      'contexts[screenSize]': criteria.screenSize,
    },
    payload: composeFiltersAsPayload(
      getDepartureDateFilters({
        departureDate,
        flexibility,
        locale: appContext.locale,
      }),
      getLocaleFilters(appContext.locale),
      getDepartureCityFilters({
        facets: criteria.filters,
        defaultValue: criteria.defaultDepartureCity,
      }),
      getDurationFilters(criteria.tripDuration),
      getProductIdsFilters(criteria.productIds),
      getFacetsFilters(criteria.filters),
      getOfferFilters(criteria.offerId),
    ),
  });
}

function getIdsFromProducts(appContext: AppContext) {
  return postSearchRequest({
    locale: appContext.locale,
    queryparams: {
      api_key: getApiKey(appContext),
      action: ACTIONS.PERTIMM_SEARCH,
      max_files: config.get('pertimm.max_files'),
      format: 'json',
      disable_catalogs: 1,
    },
    payload: composeFiltersAsPayload(getLocaleFilters(appContext.locale)),
  }).then(({ answers = [] } = {}) => answers.map((result) => get(result, 'meta.product_id[0]')));
}

export { suggest, search, searchWithFacets, getIdsFromProducts };
